// pages/AboutPage.tsx
import React, { useEffect, useReducer } from 'react';
import { Link } from 'react-router-dom';
import { useBrandingContext } from '../../../contexts/BrandingContext';
import { aboutReducer, initialAboutState } from '../model/aboutReducer';
import { formatDate } from '../../../utils/date';
import { aboutViewModel } from '../viewModel/aboutViewModel';
import { aboutApi } from '../api/aboutApi';

const AboutPage: React.FC = () => {
  const { productName } = useBrandingContext();
  const [state, dispatch] = useReducer(aboutReducer, initialAboutState);

  useEffect(() => {
    dispatch({ type: 'SET_PRODUCT_NAME', payload: productName });

    aboutViewModel.loadContent(
      dispatch,
      () => aboutApi.fetchContent(),
      'Unable to load the about content at this time.'
    );
  }, [productName]);

  const handleRetry = () => {
    aboutViewModel.loadContent(
      dispatch,
      () => aboutApi.fetchContent(),
      'Unable to load the about content at this time.'
    );
  };

  // Loading State
  if (state.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" role="status" aria-label="Loading"></div>
          <p className="text-gray-600 font-medium">Loading About Content</p>
          <p className="text-sm text-gray-500 mt-2">Please wait while we retrieve the latest information</p>
        </div>
      </div>
    );
  }

  // Error State
  if (state.error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-red-600 mb-6">
            <svg className="h-16 w-16 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Content</p>
            <p className="text-gray-600">{state.error}</p>
          </div>
          <button
            onClick={handleRetry}
            className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label="Retry loading about content"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Success State - Render Content
  return (
    <div className="bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky pt-0 pb-0 ">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between gap-4">
            <Link
              to="/"
              className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0"
              aria-label="Go back"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <span className="text-xl font-semibold text-gray-900 flex-1">
               {state.productName}
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          {/* Content Header */}
          <div className="border-b border-gray-100 bg-gray-50 px-6 py-4">
            <h1 className="text-3xl font-bold text-gray-900">About {state.productName}</h1>
            <p className="text-sm text-gray-500 mt-2">
              Effective Date: {state.model.lastUpdated ? formatDate(state.model.lastUpdated) : 'Not set'}
            </p>
          </div>

          {/* Content Body */}
          <div className="px-6 py-4">
            <div
              className="prose prose-gray max-w-none
                prose-headings:font-semibold prose-headings:text-gray-900
                prose-h1:text-2xl prose-h1:mt-8 prose-h1:mb-4
                prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-3
                prose-h3:text-lg prose-h3:mt-4 prose-h3:mb-2
                prose-p:text-gray-600 prose-p:leading-relaxed
                prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-gray-900
                prose-ul:my-4 prose-li:text-gray-600"
              dangerouslySetInnerHTML={{ __html: state.model.content }}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default AboutPage;